'use client';

import { Check, Trash2, X } from 'lucide-react';
import * as React from 'react';

import { useIsMobile } from '../hooks/use-mobile';
import { cn } from '../utils/cn';
import { Button } from './button';
import { Card, CardContent } from './card';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from './drawer';

type FormDivProps = Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'onSubmit' | 'children' | 'title'
>;

export interface InlineEditCardProps extends FormDivProps {
  /** Title shown in the card header. Omit the header entirely by leaving title, description and icon unset. */
  title?: React.ReactNode;
  /** Optional description shown under the title. */
  description?: React.ReactNode;
  /** Optional leading icon shown next to the title (e.g. <Mail className="h-4 w-4" />). */
  icon?: React.ReactNode;
  /** Called when the form is submitted (Enter or save button). */
  onSave: () => void | Promise<void>;
  /** Called when the user closes / cancels the edit. */
  onCancel: () => void;
  /** If provided, a destructive delete button is shown in the footer. */
  onDelete?: () => void;
  saveDisabled?: boolean;
  isSaving?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  deleteLabel?: string;
  /** Controls the visual wrapper.
   *  - `card` (default): full shadcn Card with padding
   *  - `bare`: lighter container for list inlays. */
  variant?: 'card' | 'bare';
  /** Extra classes on the outer wrapper (Card or bare div). */
  wrapperClassName?: string;
  /** Form content (fields). Wrapped in a space-y-4 container by default. */
  children: React.ReactNode;
  /** Hide the footer actions bar. Useful when the caller wants to render custom actions. */
  hideFooter?: boolean;
  /** Optional node placed at the very end of the footer, before the delete button. */
  extraFooter?: React.ReactNode;
  /** When true (default), the card scrolls itself fully into view after mount. Set to false to opt-out. */
  scrollIntoViewOnMount?: boolean;
  /** Scroll alignment passed to `scrollIntoView`. Defaults to `nearest` so the browser only scrolls if needed. */
  scrollBlock?: ScrollLogicalPosition;
  /** Scroll animation behaviour. Defaults to `smooth`. */
  scrollBehavior?: ScrollBehavior;
  /**
   * Force the mobile bottom-sheet presentation. When omitted, a sheet is used
   * automatically below the `md` breakpoint / on native Capacitor.
   */
  asSheet?: boolean;
}

/**
 * Card that replaces a list item / add-button with an inline editing form
 * on desktop. On mobile it opens as a keyboard-aware bottom sheet so nested
 * module cards do not shrink the fields.
 *
 * Submits on Enter, closes on Escape, and exposes Save / Cancel / Delete
 * actions with consistent visuals.
 */
export const InlineEditCard = React.forwardRef<
  HTMLFormElement,
  InlineEditCardProps
>(function InlineEditCard(
  {
    title,
    description,
    icon,
    onSave,
    onCancel,
    onDelete,
    saveDisabled = false,
    isSaving = false,
    saveLabel = 'Speichern',
    cancelLabel = 'Abbrechen',
    deleteLabel = 'Löschen',
    variant = 'card',
    wrapperClassName,
    className,
    children,
    hideFooter = false,
    extraFooter,
    scrollIntoViewOnMount = true,
    scrollBlock = 'nearest',
    scrollBehavior = 'smooth',
    asSheet,
    ...formProps
  },
  ref,
) {
  const isMobileViewport = useIsMobile();
  const sheet = asSheet ?? isMobileViewport;
  const hasHeader = Boolean(title || description || icon);
  const accessibleTitle =
    typeof title === 'string' && title.trim() ? title : 'Bearbeiten';

  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (sheet || !scrollIntoViewOnMount) return;
    const node = wrapperRef.current;
    if (!node) return;
    // Defer to the next frame so layout is settled before we measure.
    const raf = requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: scrollBehavior, block: scrollBlock });
    });
    return () => cancelAnimationFrame(raf);
  }, [sheet, scrollIntoViewOnMount, scrollBlock, scrollBehavior]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLFormElement> = (
    event,
  ) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    void onSave();
  };

  const fields = <div className="space-y-4">{children}</div>;

  const inlineFooter = hideFooter ? null : (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <Button
        type="submit"
        disabled={saveDisabled || isSaving}
        className="gap-1.5"
      >
        <Check className="h-4 w-4" />
        {saveLabel}
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={onCancel}
        disabled={isSaving}
        className="text-muted-foreground"
      >
        {cancelLabel}
      </Button>
      {extraFooter}
      {onDelete ? (
        <Button
          type="button"
          variant="ghost"
          onClick={onDelete}
          disabled={isSaving}
          className="ml-auto gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
          {deleteLabel}
        </Button>
      ) : null}
    </div>
  );

  const sheetFooter = hideFooter ? null : (
    <div className="mt-auto flex flex-col gap-2 pb-2">
      <Button
        type="submit"
        size="xl"
        disabled={saveDisabled || isSaving}
        className="w-full"
      >
        {saveLabel}
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={onCancel}
        disabled={isSaving}
        className="w-full text-muted-foreground"
      >
        {cancelLabel}
      </Button>
      {extraFooter}
      {onDelete ? (
        <Button
          type="button"
          variant="ghost"
          onClick={onDelete}
          disabled={isSaving}
          className="w-full gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
          {deleteLabel}
        </Button>
      ) : null}
    </div>
  );

  if (sheet) {
    return (
      <Drawer
        open
        onOpenChange={(open) => {
          if (!open) onCancel();
        }}
      >
        <DrawerContent keyboardAware fitContent>
          <form
            ref={ref}
            onSubmit={handleSubmit}
            onKeyDown={handleKeyDown}
            className={cn('-mx-4 flex flex-col gap-4 px-5', className)}
            {...formProps}
          >
            <div className="flex flex-row items-start justify-between gap-3 pt-2 text-left">
              <div className="min-w-0 flex-1">
                <DrawerTitle className="text-left">
                  {title ?? accessibleTitle}
                </DrawerTitle>
                {description ? (
                  <DrawerDescription className="mt-1 text-left">
                    {description}
                  </DrawerDescription>
                ) : (
                  <DrawerDescription className="sr-only">
                    {accessibleTitle}
                  </DrawerDescription>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-mr-1 h-8 w-8 shrink-0 text-muted-foreground"
                onClick={onCancel}
                disabled={isSaving}
                aria-label={cancelLabel}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {fields}
            {sheetFooter}
          </form>
        </DrawerContent>
      </Drawer>
    );
  }

  const form = (
    <form
      ref={ref}
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      className={cn('flex flex-col gap-4', className)}
      {...formProps}
    >
      {hasHeader ? (
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-2.5">
            {icon ? (
              <div className="mt-0.5 shrink-0 text-muted-foreground">
                {icon}
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              {title ? (
                <div className="truncate text-base font-semibold leading-tight">
                  {title}
                </div>
              ) : null}
              {description ? (
                <div className="mt-0.5 text-sm text-muted-foreground">
                  {description}
                </div>
              ) : null}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="-mr-1.5 -mt-1 h-8 w-8 shrink-0 text-muted-foreground"
            onClick={onCancel}
            disabled={isSaving}
            aria-label={cancelLabel}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : null}

      {fields}
      {inlineFooter}
    </form>
  );

  if (variant === 'bare') {
    return (
      <div
        ref={wrapperRef}
        className={cn(
          'rounded-lg border border-primary/50 bg-card p-4 shadow-sm ring-1 ring-primary/10 scroll-mt-4',
          wrapperClassName,
        )}
      >
        {form}
      </div>
    );
  }

  return (
    <Card
      ref={wrapperRef}
      className={cn(
        'border-primary/50 shadow-md ring-1 ring-primary/10 scroll-mt-4',
        wrapperClassName,
      )}
    >
      <CardContent className="p-4 md:p-5">{form}</CardContent>
    </Card>
  );
});
